// editor-core — framework-agnostic ES-module editor component (design D2).
//
// Owns the Ace editor, the /runner/ iframe, the Run/Stop controls and the output
// console, and ENCAPSULATES the runner postMessage contract (ported from
// public/js/embed/clientside-runner-host.js) so host pages never speak it
// directly. It depends on NO AngularJS / legacy-module-loader globals; Ace is supplied via
// opts.ace — the host bundles its own modern Ace (modern-ace-a11y) and passes it
// in (a window.ace fallback remains for tests/embedders).
//
// Public interface:
//   load(payload)          stage files + asset references (payload = { source,
//                          files, mode, assetBaseUrl, assetManifest }); `mode`
//                          selects the runner path (omit/'' => pgzero, 'pygame',
//                          'python'); also accepts the legacy load(source, assets)
//                          form (source => lone main.py)
//   getSource()            the ACTIVE file's live buffer (compat; used by Remix)
//   getFiles()             [{ name, content }] live content of every file (for Save)
//   run()                  stage the LIVE buffer then post runner:run — SYNC
//   stop()                 post runner:stop
//   restart()              force-kill: tear down/reload the runner iframe
//   on(event, cb) / off    events: 'ready' | 'state' | 'stdout' | 'stderr' |
//                          'error' | 'input-request'
//   getEditor()            the underlying Ace editor (escape hatch)
//   destroy()              detach listeners
//
//   MULTI-FILE (design D1): the editor holds one Ace EditSession per file on a
//   single editor instance, plus an activeFile. A tab strip (rendered into the
//   host-provided opts.tabsEl) swaps the active session on click — instant, and
//   each file keeps its own buffer, undo history and cursor. main.py is the entry
//   tab. A single-file trinket shows no tab chrome (visually unchanged).
//
//   AUDIO RULE (load-bearing): run() re-stages the live buffer then posts
//   runner:run with NO await/Promise/timer in between, so when it is called
//   straight from a Run-button click the user-gesture activation window is still
//   live for the runner to resume its AudioContext. editor-core wires the Run
//   button to run() directly to preserve this; hosts calling run() programmatically
//   outside a gesture simply won't get audio unlock (the run still starts).

const RUNNER_PREFIX = 'runner:';

// fix-runner-run-button-ready: bounded startup window. After the host navigates
// the iframe it arms this timer; it is disarmed on the FIRST inbound runner
// message of any type (heardFromRunner). If it fires, the runner never spoke, so
// the host surfaces a recoverable "Couldn't start the runner." fallback + Reload.
// Its own named constant, distinct from challenge-replay.js's WATCHDOG_MS (which
// only arms during a Check/replay, after the runner has already spoken).
const RUNNER_STARTUP_TIMEOUT_MS = 15000;

// Human-friendly status text per runner lifecycle state (EMBEDDING.md §4).
const STATE_TEXT = {
  booting: 'Starting…',
  running: 'Running',
  finished: 'Finished',
  stopped: 'Stopped',
  'student-error': 'Your program raised an error',
  'runner-error': 'Runner error'
};

// The contract documents message types but not every payload field name; read the
// common candidates so a minor field rename in the runner is tolerated.
function textOf(msg) {
  if (typeof msg.text === 'string') { return msg.text; }
  if (typeof msg.chunk === 'string') { return msg.chunk; }
  if (typeof msg.data === 'string') { return msg.data; }
  if (typeof msg.message === 'string') { return msg.message; }
  return '';
}

export function createEditorCore(opts) {
  opts = opts || {};

  const win = opts.window || (typeof window !== 'undefined' ? window : null);
  const ace = opts.ace || (win && win.ace);
  const frame = opts.frame;          // the /runner/ iframe element (required)
  const mount = opts.mount;          // Ace container element (required)
  const consoleEl = opts.consoleEl || null;
  const statusEl = opts.statusEl || null;
  const runBtn = opts.runBtn || null;
  const stopBtn = opts.stopBtn || null;
  const tabsEl = opts.tabsEl || null;   // host-provided tab-strip container (optional)
  // Owner-only file management (clientside-multifile-editing): when true, the tab
  // strip renders an "add file" (+) affordance plus per-file rename/delete controls
  // and an inline name prompt. Off by default so viewer/embed surfaces are unchanged;
  // the host (main.js) sets it from the same isOwner gating used for Save.
  // Read-only mode (clientside-teacher-review): the Ace buffer rejects edits
  // (setReadOnly(true)) while Run/Stop stay live — the view-only surface is still
  // re-runnable, just not editable. Off by default so the editable surfaces are
  // unchanged. File controls are forced off in this mode (you cannot add/rename/delete
  // files in a read-only view) regardless of opts.fileControls.
  const readOnly = !!opts.readOnly;
  const fileControls = !!opts.fileControls && !readOnly;
  const a11yEl = opts.a11yEl || null;   // host-provided accessibility toggle control (optional)
  const stageEl = opts.stageEl || null; // host-provided runner/canvas stage container (optional)

  if (!frame) { throw new Error('editor-core: opts.frame (runner iframe) is required'); }
  if (!ace) { throw new Error('editor-core: Ace is not available (opts.ace / window.ace)'); }
  if (!mount) { throw new Error('editor-core: opts.mount (editor element) is required'); }

  // 'stdin-submit' (code-challenge-activity): fires with the submitted line
  // whenever the student/teacher answers an input() prompt via the in-editor
  // console input row — used by the "Record Answer Key" flow to capture a
  // console script's stdin sequence.
  const listeners = { ready: [], state: [], stdout: [], stderr: [], error: [], 'input-request': [], 'stdin-submit': [], 'frame-checkpoint': [] };
  function emit(type, payload) {
    (listeners[type] || []).forEach(function (fn) {
      try { fn(payload); } catch (e) { /* a listener must not break the loop */ }
    });
  }

  // True once the runner reports runner:ready and we have staged the program.
  let loaded = false;
  // fix-runner-run-button-ready: true once ANY inbound runner message has arrived
  // (not just runner:ready). The startup timer's fire-guard is `!heardFromRunner`,
  // so a JSPI-blocked ready, a runner:error before ready, or a plain stdout all
  // prove the host is receiving the runner and disarm the fallback.
  let heardFromRunner = false;
  // Handle for the startup safety-net timer, and the dynamically-created Reload
  // affordance node (a sibling of #runner-status), tracked so they are never
  // duplicated and are cleared on recovery / teardown.
  let startupTimer = null;
  let reloadBtn = null;
  // Asset references staged alongside the source (source itself is the live buffer).
  let stagedAssets = { assetBaseUrl: '', assetManifest: [] };

  // Per-language runner execution mode (clientside-multilang-editor, design D1):
  //   '' / undefined => Pygame Zero (the default path; the field is OMITTED on
  //   runner:load), 'pygame' => plain Pygame (JSPI-gated), 'python' => CPython
  //   console with interactive input(). Captured from opts here and refreshed from
  //   load(payload).mode so the server-staged language always wins.
  let stagedMode = opts.mode || '';

  // Run can be blocked independently of lifecycle state when the browser lacks a
  // required capability — currently plain Pygame on a non-JSPI browser (design D4).
  // While blocked, Run stays disabled regardless of the staged/ready state.
  let runBlocked = false;

  // Interactive Python input (design D3): true while the program is parked in
  // input() awaiting a line (runner:input-request → runner:stdin).
  let awaitingInput = false;
  let stdinWrap = null;   // lazily-created single-line console input row
  // code-challenge-activity: resolver for the in-flight requestSnapshot() promise
  // (one at a time — Check/Record only ever have a single outstanding request).
  let pendingSnapshotResolve = null;
  // code-challenge-temporal-checkpoints: resolver for the in-flight
  // requestChallengeStep() promise (single outstanding, like the snapshot one).
  let pendingChallengeStepResolve = null;
  // code-challenge-behavioral-structural: resolver for the in-flight
  // requestChallengeTrace() promise (single outstanding, like the two above).
  let pendingChallengeTraceResolve = null;
  // code-challenge-function-io: resolver for the in-flight requestFunctionCall()
  // promise (single outstanding, like the ones above).
  let pendingFunctionCallResolve = null;
  // code-challenge-exception-checkpoints: resolver for the in-flight
  // requestConsoleRun() promise (single outstanding, like the ones above).
  let pendingConsoleRunResolve = null;
  // trace-table-capture: resolver for the in-flight requestTraceCapture() promise
  // (single outstanding, like the ones above).
  let pendingTraceCaptureResolve = null;
  let stdinInput = null;

  // Ace highlighting mode for .py files. This is the EDITOR mode (an `ace/mode/*`
  // id), distinct from `opts.mode`/`stagedMode` above (the RUNNER language). Always
  // 'ace/mode/python' — with the bundled modern Ace (modern-ace-a11y) only the
  // statically-imported modes are registered, so a bare runner-lang string like
  // 'python'/'pygame' would miss the registry and trip Ace's (basePath-less) net
  // loader, silently falling back to plain text on python/pygame trinkets.
  const PY_MODE = 'ace/mode/python';
  const TEXT_MODE = 'ace/mode/text';

  const editor = ace.edit(mount);
  editor.setTheme(opts.theme || 'ace/theme/xcode'); // matches Trinket (code-editor.js)
  editor.setShowPrintMargin(false);
  editor.setOption('useWorker', false); // no cross-origin/web-worker syntax check
  // Read-only mode is editor-level, so it persists across setSession (file switches)
  // with no per-buffer handling; Run/Stop are wired independently and stay live.
  // Guarded so a minimal Ace fake (tests/embedders) without setReadOnly still works.
  if (editor.setReadOnly) { editor.setReadOnly(readOnly); }

  // Multi-file state (design D1): one EditSession per file on this one editor.
  // `sessions` maps name -> Ace EditSession; `fileOrder` is the tab order
  // (main.py first); `activeFile` is the session currently shown in the editor.
  // `fileMeta` keeps each file's original non-content fields (e.g. `hidden`) so
  // Save round-trips them unchanged — only `content` comes from the live buffer.
  let sessions = Object.create(null);
  let fileMeta = Object.create(null);
  let fileOrder = [];
  let activeFile = null;
  // True only while editor-core is staging content programmatically (initial load,
  // draft rehydrate, restore, starter code, add/rename/delete buffer rebuilds). The
  // code-authenticity capture (code-authenticity-snapshots) reads isLoading() to
  // EXCLUDE these programmatic content changes from its paste / large-insert backstop.
  let loading = false;

  // Accessibility mode (modern-ace-a11y, design D2): a screen-reader-friendly
  // editing surface. With the bundled modern Ace (1.43.x) this flips Ace's NATIVE
  // keyboard/screen-reader support (`enableKeyboardAccessibility`) on the real
  // editor — the syntax-highlighted Ace surface is RETAINED, not swapped for a
  // plain textarea. The option is editor-level, so it persists across setSession
  // (file switches "just work") and every buffer is untouched.
  let a11yOn = false;

  // Python highlighting for .py, plain text otherwise (design D1).
  function modeForName(name) {
    return /\.py$/i.test(name || '') ? PY_MODE : TEXT_MODE;
  }

  function makeSession(name, content) {
    const session = ace.createEditSession(typeof content === 'string' ? content : '', modeForName(name));
    session.setUseSoftTabs(true);
    session.setTabSize(4);
    return session;
  }

  // Normalise the load input into an ordered [{ name, content }] with main.py
  // first. With no usable files array, synthesise a lone main.py from `source`
  // (back-compat: single-file trinkets behave exactly as before).
  function normaliseFiles(source, files) {
    let list = Array.isArray(files)
      ? files.filter(function (f) { return f && typeof f.name === 'string'; })
      : [];
    if (list.length === 0) {
      list = [{ name: 'main.py', content: typeof source === 'string' ? source : '' }];
    }
    const main = list.filter(function (f) { return f.name === 'main.py'; });
    const rest = list.filter(function (f) { return f.name !== 'main.py'; });
    return main.concat(rest); // main.py first; select the first file when absent
  }

  // (Re)build the per-file sessions from a files array and select the entry file.
  // Wrapped in the `loading` guard so the authenticity capture ignores the bulk
  // programmatic content set (initial load / rehydrate / restore / file ops).
  function buildSessions(source, files) {
    const list = normaliseFiles(source, files);
    loading = true;
    try {
      sessions = Object.create(null);
      fileMeta = Object.create(null);
      fileOrder = [];
      list.forEach(function (f) {
        if (sessions[f.name]) { return; } // ignore accidental duplicate names
        sessions[f.name] = makeSession(f.name, f.content);
        fileMeta[f.name] = f; // keep the original object for its non-content fields
        fileOrder.push(f.name);
      });
      activeFile = fileOrder[0] || null;
      if (activeFile) { editor.setSession(sessions[activeFile]); }
      renderTabs();
    } finally {
      loading = false;
    }
  }

  // Live content of every file, in tab order, each merged over its original
  // fields so non-content metadata (e.g. `hidden`) survives a Save round-trip.
  function collectFiles() {
    return fileOrder.map(function (name) {
      const out = {};
      const meta = fileMeta[name];
      if (meta) {
        for (const k in meta) {
          if (Object.prototype.hasOwnProperty.call(meta, k)) { out[k] = meta[k]; }
        }
      }
      out.name = name;
      out.content = sessions[name].getValue();
      return out;
    });
  }

  // Normalise a canned-stdin array to plain input lines. Two shapes reach the
  // console-run / trace passes: a stored checkpoint `triggerStdin` (already an
  // array of strings) and a freshly-recorded `recordedInputs` (an array of
  // challenge-replay event objects `{ kind:'stdin', text, atMs }`). Accept both —
  // strings ride through; stdin event objects contribute their `text`; anything
  // else (e.g. a stray pgzero pointer event) is dropped so it can't feed a blank
  // line into the program.
  function toStdinLines(arr) {
    if (!Array.isArray(arr)) { return []; }
    const out = [];
    arr.forEach(function (e) {
      if (typeof e === 'string') { out.push(e); }
      else if (e && typeof e === 'object' && typeof e.text === 'string') { out.push(e.text); }
    });
    return out;
  }

  // Switch the active file: swap the editor's session (instant; preserves each
  // file's buffer, undo history and cursor) and re-render the strip. Accessibility
  // mode needs no special-casing — `enableKeyboardAccessibility` is editor-level
  // and survives setSession, so the new buffer stays accessible automatically.
  function setActiveFile(name) {
    if (!sessions[name] || name === activeFile) { return; }
    activeFile = name;
    editor.setSession(sessions[name]);
    renderTabs();
  }

  // Validate a created/renamed file name BEFORE applying it (design D6), mirroring
  // the runner's own name-safety rules so an unsafe name is caught in the dialog
  // rather than as a runner error. Rejects empty/whitespace-only, duplicate (among
  // fileOrder, excluding `ignoreName` so a rename to its own name is a no-op), a
  // leading '/', a '..' path segment, a backslash, or a NUL. Returns { ok, reason }.
  function validateFileName(name, ignoreName) {
    if (typeof name !== 'string' || name.trim() === '') {
      return { ok: false, reason: 'Enter a file name.' };
    }
    if (name.indexOf('\0') !== -1) {
      return { ok: false, reason: 'Name cannot contain a NUL character.' };
    }
    if (name.indexOf('\\') !== -1) {
      return { ok: false, reason: 'Use "/" instead of "\\" in file names.' };
    }
    if (name.charAt(0) === '/') {
      return { ok: false, reason: 'File name cannot start with "/".' };
    }
    const isDotDot = name.split('/').some(function (seg) { return seg === '..'; });
    if (isDotDot) {
      return { ok: false, reason: 'File name cannot contain "..".' };
    }
    const exists = fileOrder.some(function (n) { return n === name && n !== ignoreName; });
    if (exists) {
      return { ok: false, reason: 'A file named "' + name + '" already exists.' };
    }
    return { ok: true };
  }

  // Add a new file (design D5): validate, create a session (extension drives the
  // mode), append to the tab order, and make it active. Buffer-level only — it is
  // persisted by the existing getFiles() -> Save round-trip. Returns { ok, reason }.
  function addFile(name) {
    const v = validateFileName(name);
    if (!v.ok) { return v; }
    sessions[name] = makeSession(name, '');
    fileMeta[name] = { name: name, content: '' };
    fileOrder.push(name);
    setActiveFile(name); // swaps the session (and rebinds a11y) + re-renders the strip
    return { ok: true };
  }

  // Rename a file other than main.py (design D5/D6). Re-keys the SAME EditSession so
  // the buffer, undo history and cursor are preserved, re-applies the extension mode,
  // keeps the active selection, and re-renders. Returns { ok, reason }.
  function renameFile(oldName, newName) {
    if (oldName === 'main.py') {
      return { ok: false, reason: 'main.py is the entry file and cannot be renamed.' };
    }
    if (!sessions[oldName]) {
      return { ok: false, reason: 'There is no file named "' + oldName + '".' };
    }
    if (newName === oldName) { return { ok: true }; } // no-op rename
    const v = validateFileName(newName, oldName);
    if (!v.ok) { return v; }
    const session = sessions[oldName];
    if (session.setMode) { session.setMode(modeForName(newName)); } // re-highlight by ext
    sessions[newName] = session;
    delete sessions[oldName];
    // Copy the meta under the new name rather than mutating the caller's file object
    // (fileMeta holds references to the host's input array; mutating .name in place
    // would surprise a host that reuses that array). Non-content fields ride along.
    const oldMeta = fileMeta[oldName] || {};
    const newMeta = {};
    for (const k in oldMeta) {
      if (Object.prototype.hasOwnProperty.call(oldMeta, k)) { newMeta[k] = oldMeta[k]; }
    }
    newMeta.name = newName;
    fileMeta[newName] = newMeta;
    delete fileMeta[oldName];
    const idx = fileOrder.indexOf(oldName);
    if (idx >= 0) { fileOrder[idx] = newName; }
    if (activeFile === oldName) { activeFile = newName; }
    renderTabs();
    return { ok: true };
  }

  // Delete a file other than main.py (design D5). Drops its session/meta/order entry;
  // if it was active, falls back to main.py (or the first remaining file). Returns
  // { ok, reason }.
  function deleteFile(name) {
    if (name === 'main.py') {
      return { ok: false, reason: 'main.py is the entry file and cannot be deleted.' };
    }
    if (!sessions[name]) {
      return { ok: false, reason: 'There is no file named "' + name + '".' };
    }
    const wasActive = activeFile === name;
    delete sessions[name];
    delete fileMeta[name];
    const idx = fileOrder.indexOf(name);
    if (idx >= 0) { fileOrder.splice(idx, 1); }
    if (wasActive) {
      activeFile = sessions['main.py'] ? 'main.py' : (fileOrder[0] || null);
      if (activeFile) {
        editor.setSession(sessions[activeFile]);
      }
    }
    renderTabs();
    return { ok: true };
  }

  // The host control may be a checkbox (a settings-panel row) or a button (a toolbar
  // toggle). Detect once so wiring + state sync use the right mechanism.
  function a11yElIsCheckbox() {
    return !!a11yEl && (a11yEl.type === 'checkbox' ||
      (a11yEl.getAttribute && a11yEl.getAttribute('type') === 'checkbox'));
  }

  function updateA11yControl() {
    if (!a11yEl) { return; }
    if (a11yElIsCheckbox()) {
      a11yEl.checked = a11yOn;                 // native on/off state
    } else if (a11yEl.setAttribute) {
      a11yEl.setAttribute('aria-pressed', a11yOn ? 'true' : 'false');
    }
  }

  // Toggle the screen-reader accessibility mode by flipping Ace's native
  // `enableKeyboardAccessibility` option (modern-ace-a11y, design D2). The rich
  // syntax-highlighted surface stays in place; the option is editor-level so it
  // persists across file switches with no buffer touch. The host toggle control's
  // state (aria-pressed / checked) is kept in sync.
  function setAccessibilityMode(on) {
    a11yOn = !!on;
    editor.setOption('enableKeyboardAccessibility', a11yOn);
    updateA11yControl();
  }

  // Wire the optional host toggle control (mirrors how tabsEl is host-provided). Both
  // a checkbox (its `change`/`checked`) and a button (its `click`, with aria-pressed)
  // are supported; both are keyboard-reachable natively. A checkbox takes its
  // accessible name from its <label>, so we only add a default aria-label to a button.
  let a11yElHandler = null;
  let a11yElEvent = null;
  if (a11yEl) {
    if (a11yElIsCheckbox()) {
      a11yElEvent = 'change';
      a11yElHandler = function () { setAccessibilityMode(!!a11yEl.checked); };
    } else {
      a11yElEvent = 'click';
      if (a11yEl.setAttribute) {
        if (!a11yEl.getAttribute || !a11yEl.getAttribute('aria-label')) {
          a11yEl.setAttribute('aria-label', 'Toggle screen-reader accessibility mode');
        }
        a11yEl.setAttribute('aria-pressed', 'false');
      }
      a11yElHandler = function () { setAccessibilityMode(!a11yOn); };
    }
    a11yEl.addEventListener(a11yElEvent, a11yElHandler);
  }

  // Inline file-name prompt (design D6): a one-field form inserted after the tab
  // strip. Used for both add and rename so we never call the browser alert/confirm
  // (which would break the isolated-frame contract — task 2.3). Validation failures
  // are shown inline in its error span; success applies the op and hides the prompt.
  let filePrompt = null; // { wrap, input, error, mode, oldName } once built

  function ensureFilePrompt() {
    if (filePrompt || !tabsEl) { return filePrompt; }
    const doc = tabsEl.ownerDocument || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.createElement) { return null; }
    const wrap = doc.createElement('form');
    wrap.className = 'editor-file-prompt';
    wrap.hidden = true;
    const input = doc.createElement('input');
    input.type = 'text';
    input.className = 'editor-file-prompt-input';
    input.setAttribute('aria-label', 'File name');
    input.setAttribute('autocomplete', 'off');
    const error = doc.createElement('span');
    error.className = 'editor-file-prompt-error';
    error.setAttribute('aria-live', 'polite');
    if (wrap.appendChild) { wrap.appendChild(input); wrap.appendChild(error); }
    // Submit (Enter) applies the op; preventDefault keeps the page from reloading.
    wrap.addEventListener('submit', function (e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      submitFilePrompt();
    });
    // Esc cancels without changing the file set.
    wrap.addEventListener('keydown', function (e) {
      if (e && (e.key === 'Escape' || e.keyCode === 27)) { hideFilePrompt(); }
    });
    if (tabsEl.parentNode && tabsEl.parentNode.insertBefore) {
      tabsEl.parentNode.insertBefore(wrap, tabsEl.nextSibling || null);
    } else if (tabsEl.appendChild) {
      tabsEl.appendChild(wrap);
    }
    filePrompt = { wrap: wrap, input: input, error: error, mode: 'add', oldName: null };
    return filePrompt;
  }

  function showFilePrompt(mode, oldName) {
    if (!ensureFilePrompt()) { return; }
    filePrompt.mode = mode;
    filePrompt.oldName = oldName || null;
    filePrompt.error.textContent = '';
    filePrompt.input.value = (mode === 'rename') ? (oldName || '') : '';
    filePrompt.wrap.hidden = false;
    if (filePrompt.input.focus) { filePrompt.input.focus(); }
    if (mode === 'rename' && filePrompt.input.select) { filePrompt.input.select(); }
  }

  function hideFilePrompt() {
    if (filePrompt) {
      filePrompt.wrap.hidden = true;
      filePrompt.error.textContent = '';
    }
  }

  function submitFilePrompt() {
    if (!filePrompt) { return; }
    const name = filePrompt.input.value;
    const res = (filePrompt.mode === 'rename')
      ? renameFile(filePrompt.oldName, name)
      : addFile(name);
    if (res && res.ok) {
      hideFilePrompt();
    } else {
      filePrompt.error.textContent = (res && res.reason) || 'Invalid file name.';
    }
  }

  // Render the tab strip into the host container. Hidden when ≤1 file so viewer
  // surfaces are visually identical to before (design D5) — UNLESS file controls are
  // on, in which case owners always see the strip (with the add affordance) even for
  // a single-file trinket so they can add the first sibling.
  function renderTabs() {
    if (!tabsEl) { return; }
    while (tabsEl.firstChild) { tabsEl.removeChild(tabsEl.firstChild); }
    if (fileOrder.length <= 1 && !fileControls) {
      tabsEl.hidden = true;
      return;
    }
    tabsEl.hidden = false;
    const doc = tabsEl.ownerDocument || (typeof document !== 'undefined' ? document : null);
    if (!doc) { return; }
    fileOrder.forEach(function (name) {
      const tab = doc.createElement('button');
      tab.type = 'button';
      tab.className = 'editor-tab' + (name === activeFile ? ' is-active' : '');
      tab.textContent = name;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', name === activeFile ? 'true' : 'false');
      tab.addEventListener('click', function () { setActiveFile(name); });
      // Owner-only per-file rename/delete affordances, hidden for the main.py entry.
      // Guarded by tab.appendChild so the lightweight test fakes (no appendChild on a
      // button) keep working — the controls only render against a real DOM.
      if (fileControls && name !== 'main.py' && tab.appendChild) {
        const ren = doc.createElement('span');
        ren.className = 'editor-tab-action editor-tab-rename';
        ren.setAttribute('role', 'button');
        ren.setAttribute('tabindex', '0');
        ren.setAttribute('aria-label', 'Rename ' + name);
        ren.title = 'Rename';
        ren.textContent = '✎'; // pencil
        ren.addEventListener('click', function (e) {
          if (e && e.stopPropagation) { e.stopPropagation(); }
          showFilePrompt('rename', name);
        });
        const del = doc.createElement('span');
        del.className = 'editor-tab-action editor-tab-delete';
        del.setAttribute('role', 'button');
        del.setAttribute('tabindex', '0');
        del.setAttribute('aria-label', 'Delete ' + name);
        del.title = 'Delete';
        del.textContent = '×'; // ×
        del.addEventListener('click', function (e) {
          if (e && e.stopPropagation) { e.stopPropagation(); }
          deleteFile(name);
        });
        tab.appendChild(ren);
        tab.appendChild(del);
      }
      tabsEl.appendChild(tab);
    });
    // The "add file" (+) affordance lives at the end of the strip (task 2.1).
    if (fileControls) {
      const add = doc.createElement('button');
      add.type = 'button';
      add.className = 'editor-tab-add';
      add.setAttribute('aria-label', 'Add file');
      add.title = 'Add file';
      add.textContent = '+';
      add.addEventListener('click', function () { showFilePrompt('add', null); });
      tabsEl.appendChild(add);
    }
  }

  // host -> runner. Same-origin, so '*' is safe here; pin to the runner's exact
  // origin before any cross-origin deployment (EMBEDDING.md §8).
  function post(msg) {
    if (frame.contentWindow) {
      frame.contentWindow.postMessage(msg, '*');
    }
  }

  function setStatus(text, isError) {
    if (!statusEl) { return; }
    statusEl.textContent = text;
    if (isError) {
      statusEl.classList.add('runner-error');
    } else {
      statusEl.classList.remove('runner-error');
    }
  }

  // fix-runner-run-button-ready — startup safety net -----------------------
  // Cancel any pending startup timer (recovery, restart, teardown).
  function clearStartupTimer() {
    if (startupTimer !== null) {
      clearTimeout(startupTimer);
      startupTimer = null;
    }
  }

  // Remove the dynamically-created Reload affordance if present.
  function removeReloadButton() {
    if (reloadBtn && reloadBtn.parentNode && reloadBtn.parentNode.removeChild) {
      reloadBtn.parentNode.removeChild(reloadBtn);
    }
    reloadBtn = null;
  }

  // The runner produced no message within the startup window. Announce a
  // recoverable error via #runner-status (aria-live) and present a Reload control
  // as a SIBLING of the status region (never inside the aria-live region). Reuse
  // the existing .runner-btn design tokens; wire it to restart(). Existence-guard
  // so a repeat fire reuses the node rather than appending a duplicate.
  function showStartupFallback() {
    setStatus('Couldn\'t start the runner. Press Reload to try again.', true);
    if (reloadBtn || !statusEl || !statusEl.parentNode) { return; }
    const doc = statusEl.ownerDocument || (typeof document !== 'undefined' ? document : null);
    if (!doc) { return; }
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'runner-btn';
    btn.textContent = 'Reload';
    btn.setAttribute('aria-label', 'Reload');
    btn.addEventListener('click', function () { restart(); });
    // Sibling of #runner-status, not a child of the aria-live region.
    if (statusEl.nextSibling) {
      statusEl.parentNode.insertBefore(btn, statusEl.nextSibling);
    } else {
      statusEl.parentNode.appendChild(btn);
    }
    reloadBtn = btn;
  }

  // Arm the startup timer. Fires only if we still haven't heard from the runner.
  function armStartupTimer() {
    clearStartupTimer();
    startupTimer = setTimeout(function () {
      startupTimer = null;
      if (!heardFromRunner) { showStartupFallback(); }
    }, RUNNER_STARTUP_TIMEOUT_MS);
  }

  // Force-kill: reloading the iframe terminates a wedged worker (stronger than
  // runner:stop). The runner re-emits runner:ready, which re-stages the buffer.
  // Reused by both the public API and the Reload affordance.
  function restart() {
    loaded = false;
    heardFromRunner = false;
    removeReloadButton();
    setStatus('Loading runner…', false);
    setButtons(false, false);
    if (consoleEl) { consoleEl.textContent = ''; }
    frame.src = runnerSrc;
    armStartupTimer();
  }

  // Run is enabled whenever the program is staged (it doubles as restart while a
  // run is live); Stop is enabled only while a run is booting or running. A
  // capability block (e.g. plain Pygame without JSPI, design D4) force-disables Run
  // regardless of the staged/ready state.
  function setButtons(canRun, canStop) {
    if (runBtn) { runBtn.disabled = !canRun || runBlocked; }
    if (stopBtn) { stopBtn.disabled = !canStop; }
  }

  // Apply the mode-driven primary surface (design D3 / task 4.1): Python is a
  // console program, so hide the canvas/runner stage and let the host-side console
  // be the primary surface; Pygame / Pygame Zero keep the canvas. The worker still
  // lives in the (now hidden) iframe, so execution and stdout/stderr are unaffected.
  function applySurface() {
    if (!stageEl || !stageEl.style) { return; }
    stageEl.style.display = (stagedMode === 'python') ? 'none' : '';
  }

  // Lazily build the single-line interactive-input row inside the console wrap
  // (design D3). Created on the first runner:input-request so non-Python surfaces
  // stay DOM-light. Submitting (Enter) posts one runner:stdin { text } line; the
  // transcript (prompt + typed line) comes back over stdout from the runner itself,
  // so this row never writes to the console.
  function ensureStdin() {
    if (stdinWrap || !consoleEl) { return stdinWrap; }
    const doc = consoleEl.ownerDocument || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.createElement) { return null; }
    const wrap = doc.createElement('form');
    wrap.className = 'runner-stdin';
    wrap.hidden = true;
    const input = doc.createElement('input');
    input.type = 'text';
    input.className = 'runner-stdin-input';
    input.setAttribute('aria-label', 'Program input');
    input.setAttribute('autocomplete', 'off');
    wrap.appendChild(input);
    // Submit (Enter) delivers the line; preventDefault keeps the page from reloading.
    wrap.addEventListener('submit', function (e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      submitStdin();
    });
    const parent = consoleEl.parentNode;
    if (parent && parent.appendChild) { parent.appendChild(wrap); }
    stdinWrap = wrap;
    stdinInput = input;
    return wrap;
  }

  function showStdin(prompt) {
    if (!ensureStdin()) { return; }
    awaitingInput = true;
    // Do NOT echo the prompt into the transcript: the runner ALREADY streams it as
    // a normal stdout chunk (_pyrunner_host.py routes python_runner's input_prompt
    // part through emit('stdout', …), exactly as CPython echoes it at a terminal),
    // so printing it here too rendered every prompt twice. The request's `prompt`
    // is metadata for the input affordance only — the same treatment the in-frame
    // standalone console gives it (runner-core.js showInputLine).
    stdinWrap.hidden = false;
    if (stdinInput) {
      stdinInput.value = '';
      stdinInput.placeholder = (typeof prompt === 'string') ? prompt : '';
      if (stdinInput.focus) { stdinInput.focus(); }
    }
  }

  function hideStdin() {
    awaitingInput = false;
    if (stdinWrap) { stdinWrap.hidden = true; }
  }

  function submitStdin() {
    if (!awaitingInput || !stdinInput) { return; }
    const text = stdinInput.value;
    // No local echo here either: python_runner emits the entered line back as an
    // `input` output part (runner.py readline -> self.output('input', value)), which
    // _pyrunner_host.py streams as stdout — echoing it here printed it twice.
    post({ type: 'runner:stdin', text: text });
    emit('stdin-submit', text); // code-challenge-activity: recording capture hook
    stdinInput.value = '';
    hideStdin();
  }

  function appendConsole(text, stream) {
    if (!consoleEl || text === undefined || text === null) { return; }
    // Sticky auto-scroll: only pin to the newest line if the user is already at (or
    // within a line-height of) the bottom, so scrolling up to read earlier output is
    // not yanked back on the next chunk. NaN-safe `!(… > n)` defaults to pinning when
    // scroll metrics are unavailable (e.g. a bare test fake) — preserving old behaviour.
    const stick = !((consoleEl.scrollHeight - consoleEl.scrollTop - consoleEl.clientHeight) > 24);
    const span = (consoleEl.ownerDocument || document).createElement('span');
    if (stream === 'stderr') { span.className = 'stderr'; }
    span.textContent = String(text);
    consoleEl.appendChild(span);
    if (stick) { consoleEl.scrollTop = consoleEl.scrollHeight; }
  }

  // Stage the program in the runner: the LIVE content of EVERY file plus the
  // server-staged asset references. Staging does not run the program (design D2).
  // `files` carries every live buffer so the worker writes each into /game/ and
  // sibling imports resolve; `source` stays = main.py's live content for a
  // back-compatible single-file fallback (an un-bumped runner ignores `files` and
  // still runs `source`). mode is omitted (=> pgzero).
  function stageLoad() {
    const files = collectFiles();
    const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
    const load = {
      type: 'runner:load',
      source: main ? main.content : editor.getValue(),
      files: files,
      assetBaseUrl: stagedAssets.assetBaseUrl || '',
      assetManifest: stagedAssets.assetManifest || []
    };
    // Omit `mode` entirely for Pygame Zero (the default path); only set it for the
    // additive pygame/python paths (design D1). An un-bumped runner ignores it.
    if (stagedMode) { load.mode = stagedMode; }
    post(load);
  }

  function onMessage(event) {
    // Only trust messages from our runner iframe.
    if (!frame.contentWindow || event.source !== frame.contentWindow) { return; }
    const msg = event.data;
    if (!msg || typeof msg.type !== 'string' || msg.type.indexOf(RUNNER_PREFIX) !== 0) { return; }

    // fix-runner-run-button-ready: the FIRST inbound runner message of any type
    // proves the host is receiving the runner, so disarm the startup safety net.
    // If the fallback was showing, remove the Reload button and reset the status
    // to a neutral non-error state BEFORE this message's own handler runs — so a
    // first message whose handler does not call setStatus (e.g. runner:stdout,
    // runner:live-frame-checkpoint) cannot leave stale fallback text lingering
    // next to a now-working runner. The ready/error handlers overwrite it as usual.
    if (!heardFromRunner) {
      heardFromRunner = true;
      clearStartupTimer();
      if (reloadBtn) {
        removeReloadButton();
        setStatus('Loading runner…', false);
      }
    }

    switch (msg.type) {
      case 'runner:ready': {
        loaded = true;
        // Capability gate (design D4): plain Pygame needs WebAssembly JSPI
        // (Chromium). When it is unavailable, block Run with a clear message rather
        // than letting the program hang. Pygame Zero and Python are NEVER gated.
        const jspiOK = !msg.capabilities || msg.capabilities.jspi !== false;
        if (stagedMode === 'pygame' && !jspiOK) {
          runBlocked = true;
          stageLoad();
          setStatus('Plain Pygame needs a Chromium-based browser (Chrome or Edge).', true);
          setButtons(false, false);
        } else {
          stageLoad();
          setStatus('Ready — press Run.', false);
          setButtons(true, false);
        }
        applySurface();
        emit('ready');
        break;
      }

      case 'runner:state':
        if (msg.state && STATE_TEXT[msg.state]) {
          setStatus(STATE_TEXT[msg.state], msg.state === 'runner-error' || msg.state === 'student-error');
        } else if (msg.state) {
          setStatus(String(msg.state), false);
        }
        if (msg.state === 'booting' || msg.state === 'running') {
          setButtons(loaded, true);
        } else {
          setButtons(loaded, false);
        }
        // Any terminal/idle state means the program is no longer parked in input()
        // (a Stop while parked ends as `stopped`, handled by the runner — design D3).
        if (msg.state !== 'running') { hideStdin(); }
        emit('state', msg.state);
        break;

      case 'runner:input-request':
        // The Python program is blocked in input()/readline(); reveal and focus the
        // single-line console input. The reply goes back as runner:stdin (design D3).
        showStdin(typeof msg.prompt === 'string' ? msg.prompt : '');
        emit('input-request', msg.prompt);
        break;

      case 'runner:stdout':
        appendConsole(textOf(msg), 'stdout');
        emit('stdout', textOf(msg));
        break;

      case 'runner:stderr':
        appendConsole(textOf(msg), 'stderr');
        emit('stderr', textOf(msg));
        break;

      case 'runner:error': {
        // Surface the failure rather than leaving a blank frame. kind:"runner" is
        // our infrastructure (e.g. lost isolation); kind:"student" is the program's
        // own uncaught exception. Both are shown prominently in the status line.
        const detail = msg.detail || textOf(msg) || 'Unknown error';
        const kind = msg.kind || 'runner';
        setStatus('Error (' + kind + '): ' + detail, true);
        appendConsole('\n' + detail + '\n', 'stderr');
        emit('error', { kind: kind, detail: detail });
        break;
      }

      case 'runner:snapshot-result':
        // code-challenge-activity: resolve the pending requestSnapshot() promise,
        // if any (a stray/late result with nothing awaiting it is just dropped).
        if (pendingSnapshotResolve) {
          const resolve = pendingSnapshotResolve;
          pendingSnapshotResolve = null;
          resolve(msg.values || {});
        }
        break;

      case 'runner:challenge-step-result':
        // code-challenge-temporal-checkpoints: resolve the pending
        // requestChallengeStep() promise with { series, finalValues } (R11).
        // code-challenge-flag-state-checkpoints (0.14.0): also carry the additive
        // discreteSeries (bool/string per-frame history); absent on older runners.
        // A stray/late result with nothing awaiting it is dropped.
        if (pendingChallengeStepResolve) {
          const resolveStep = pendingChallengeStepResolve;
          pendingChallengeStepResolve = null;
          resolveStep({
            series: msg.series || {},
            finalValues: msg.finalValues || {},
            discreteSeries: msg.discreteSeries || {},
            // code-challenge-dotted-path-checkpoints (0.16.0): additive
            // discovered-path menu [{path, leafType}]; absent on older runners.
            discoveredPaths: msg.discoveredPaths || []
          });
        }
        break;

      case 'runner:challenge-trace-result':
        // code-challenge-behavioral-structural: resolve the pending
        // requestChallengeTrace() promise with { stdout, finalValues, behavioral,
        // structural }. A stray/late result with nothing awaiting it is dropped.
        if (pendingChallengeTraceResolve) {
          const resolveTrace = pendingChallengeTraceResolve;
          pendingChallengeTraceResolve = null;
          resolveTrace({
            stdout: msg.stdout || '',
            finalValues: msg.finalValues || {},
            behavioral: msg.behavioral || {},
            structural: msg.structural || {}
          });
        }
        break;

      case 'runner:challenge-fncall-result':
        // code-challenge-function-io: resolve the pending requestFunctionCall()
        // promise with { results, functions }. A stray/late result with nothing
        // awaiting it is dropped.
        if (pendingFunctionCallResolve) {
          const resolveFn = pendingFunctionCallResolve;
          pendingFunctionCallResolve = null;
          resolveFn({ results: msg.results || [], functions: msg.functions || [] });
        }
        break;

      case 'runner:challenge-console-run-result':
        // code-challenge-exception-checkpoints: resolve the pending
        // requestConsoleRun() promise with the raw console-run facts. A stray/late
        // result with nothing awaiting it is dropped.
        if (pendingConsoleRunResolve) {
          const resolveCr = pendingConsoleRunResolve;
          pendingConsoleRunResolve = null;
          resolveCr({
            stdout: msg.stdout || '',
            exception: msg.exception || null,
            inputReads: msg.inputReads || 0,
            outputAfterLastInput: !!msg.outputAfterLastInput,
            ranOutOfInput: !!msg.ranOutOfInput,
            // code-challenge-stdin-transcript (grading v3 [3/3]): additive
            // interleaved transcript for prompt-independent multi-case grading.
            transcript: msg.transcript || null
          });
        }
        break;

      case 'runner:trace-capture-result':
        // trace-table-capture: resolve the pending requestTraceCapture() promise
        // with { variables, rows, truncated, stdout, error, outcome }. A stray/late
        // result with nothing awaiting it is dropped.
        if (pendingTraceCaptureResolve) {
          const resolveTt = pendingTraceCaptureResolve;
          pendingTraceCaptureResolve = null;
          resolveTt({
            variables: msg.variables || [],
            rows: msg.rows || [],
            truncated: !!msg.truncated,
            stdout: msg.stdout || '',
            error: msg.error || null,
            outcome: msg.outcome || 'finished'
          });
        }
        break;

      case 'runner:live-frame-checkpoint':
        // live-frame-index-tracking: the worker reports ONLY a frame number
        // (its own clock is a separate, non-comparable realm); the timestamp
        // is stamped HERE, on receipt, using the host's own performance.now()
        // — the same realm challenge-replay.js's atMs() helper already uses.
        emit('frame-checkpoint', { frame: msg.frame, atMs: performance.now() });
        break;

      default:
        break;
    }
  }

  win.addEventListener('message', onMessage);

  // fix-runner-run-button-ready — listen-before-navigate (the core race fix).
  // The listener above is attached synchronously (before any await), so we now
  // navigate the iframe OURSELVES rather than relying on an eager template `src`.
  // This guarantees the one-shot runner:ready cannot be posted before we listen.
  // The URL comes from the template's data-runner-src; a literal `src` (e.g. a
  // smoke fixture) is tolerated as a fallback and simply re-navigated once,
  // post-listener. runnerSrc is stored for restart(). The assignment is
  // unconditional and still begins with the /runner/ isolation prefix, so
  // COOP/COEP continue to apply.
  const runnerSrc = frame.dataset.runnerSrc || frame.getAttribute('src');
  if (runnerSrc) {
    frame.src = runnerSrc;
    armStartupTimer();
  }

  // Run: re-stage the LIVE buffer then post runner:run — both SYNCHRONOUSLY so a
  // Run-button click keeps the gesture activation live for the runner's audio
  // unlock. Re-staging a fresh source while a run is live restarts from it
  // (running -> booting -> running), so Run doubles as restart with latest edits.
  // Clear the console first so each run shows only its own output (not a stale
  // traceback from the previous run).
  function run() {
    if (!loaded || runBlocked) { return; }
    if (consoleEl) { consoleEl.textContent = ''; }
    hideStdin();
    stageLoad();
    post({ type: 'runner:run' });
  }

  // Stop: graceful stop; no gesture/origin constraint, safe no-op when idle.
  function stop() {
    post({ type: 'runner:stop' });
  }

  if (runBtn) { runBtn.addEventListener('click', run); }
  if (stopBtn) { stopBtn.addEventListener('click', stop); }

  return {
    // Stage the initial files + asset references and build the per-file sessions.
    // New form: load(payload) where payload = { source, files, assetBaseUrl,
    // assetManifest }. Legacy form (back-compat): load(source, assets) — `source`
    // is a string and becomes a lone main.py. If the runner is already ready,
    // re-stages immediately.
    load: function (a, b) {
      let source = '';
      let files = null;
      let assets;
      if (a && typeof a === 'object' && !Array.isArray(a)) {
        source = typeof a.source === 'string' ? a.source : '';
        files = Array.isArray(a.files) ? a.files : null;
        assets = a; // assetBaseUrl / assetManifest live on the payload
      } else {
        source = typeof a === 'string' ? a : '';
        assets = b || {};
      }
      stagedAssets = {
        assetBaseUrl: (assets && assets.assetBaseUrl) || '',
        assetManifest: (assets && assets.assetManifest) || []
      };
      // The server-staged language mode (payload.mode) wins over the construction-
      // time opts.mode, so the running language always matches what the page served.
      if (assets && typeof assets.mode === 'string') { stagedMode = assets.mode; }
      buildSessions(source, files);
      applySurface();
      if (loaded) { stageLoad(); }
    },
    getSource: function () { return editor.getValue(); },
    // Live content of every file (for Save / multi-file Run), in tab order.
    getFiles: function () { return collectFiles(); },
    // Refresh the staged asset references after a media upload/removal (design D3,
    // task 4.4). The server rebuilds the manifest (single source of truth) and the
    // host passes it here so the NEXT stageLoad/Run includes the change; if the
    // runner is already ready we re-stage immediately so the manifest is current.
    setAssets: function (assets) {
      stagedAssets = {
        assetBaseUrl: (assets && assets.assetBaseUrl) || '',
        assetManifest: (assets && assets.assetManifest) || []
      };
      if (loaded) { stageLoad(); }
    },
    // File management (clientside-multifile-editing, design D5). Each validates and
    // returns { ok, reason } so the caller (the inline prompt, or a host/test) can
    // surface a failure. main.py is protected from rename/delete.
    addFile: function (name) { return addFile(name); },
    renameFile: function (oldName, newName) { return renameFile(oldName, newName); },
    deleteFile: function (name) { return deleteFile(name); },
    // Accessibility mode (modern-ace-a11y, design D2): toggle Ace's native
    // screen-reader/keyboard support, and read the current state. Host controls call
    // these; the optional opts.a11yEl is wired to setAccessibilityMode automatically.
    setAccessibilityMode: function (on) { setAccessibilityMode(on); },
    isAccessibilityMode: function () { return a11yOn; },
    run: run,
    stop: stop,
    // --- Code Challenge grading support (code-challenge-activity) -----------
    // Three narrow, purpose-built additions used ONLY by the Record-Answer-Key
    // / Check flows — normal Run/Save/Share etc. are completely untouched.
    //
    // Re-stage the live buffer + run it ONCE with a deterministic random seed,
    // WITHOUT persisting the seed onto the normally-staged program — so a
    // student's later plain Run (after a Check replay) gets real randomness
    // again, exactly as today (decisions.md: "normal free-play Run/assignment
    // execution is unaffected"). Mirrors run()'s synchronous staging so the
    // console/canvas clears the same way a normal Run does.
    runWithChallengeSeed: function (challengeSeed) {
      if (!loaded || runBlocked) { return; }
      if (consoleEl) { consoleEl.textContent = ''; }
      hideStdin();
      const files = collectFiles();
      const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
      const load = {
        type: 'runner:load',
        source: main ? main.content : editor.getValue(),
        files: files,
        assetBaseUrl: stagedAssets.assetBaseUrl || '',
        assetManifest: stagedAssets.assetManifest || [],
        challengeSeed: challengeSeed
      };
      if (stagedMode) { load.mode = stagedMode; }
      post(load);
      post({ type: 'runner:run' });
    },
    // Request named top-level variables from the executed module's namespace
    // (empty/absent `variables` ⇒ discover every plain top-level variable —
    // used once, right after a "Record Answer Key" session stops, to build the
    // tickable checklist). Resolves with the filtered `{ values }` object; never
    // rejects (a runner-side failure simply resolves to `{}`).
    requestSnapshot: function (variables) {
      return new Promise(function (resolve) {
        pendingSnapshotResolve = resolve;
        post({ type: 'runner:snapshot-request', variables: variables || [] });
      });
    },
    // code-challenge-temporal-checkpoints: run the live buffer ONCE through the
    // deterministic frame-stepping driver (Pygame Zero temporal grading) and
    // resolve with { series, finalValues } (R11). Re-stages the live buffer with
    // an additive `challengeStep` load option then Runs — mirrors
    // runWithChallengeSeed's synchronous staging. `dt` is worker-authoritative
    // (NOT an argument, R2). Never rejects: a runner failure leaves the promise
    // pending until the caller's own watchdog/terminal-state handling fires, so
    // the Check flow drives it off runner state as well (challenge-chrome.js).
    // code-challenge-dotted-path-checkpoints: `discoverPaths` (5th, optional,
    // default false) is threaded into the challengeStep payload — when true the
    // runner enumerates reachable object/list/dict paths and the resolved promise
    // additionally carries `discoveredPaths`. Absent/false ⇒ unchanged.
    requestChallengeStep: function (variables, frameCap, recordedInputs, challengeSeed, discoverPaths) {
      return new Promise(function (resolve) {
        pendingChallengeStepResolve = resolve;
        if (!loaded || runBlocked) { return; }
        if (consoleEl) { consoleEl.textContent = ''; }
        hideStdin();
        const files = collectFiles();
        const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
        const load = {
          type: 'runner:load',
          source: main ? main.content : editor.getValue(),
          files: files,
          assetBaseUrl: stagedAssets.assetBaseUrl || '',
          assetManifest: stagedAssets.assetManifest || [],
          challengeSeed: challengeSeed,
          challengeStep: {
            variables: variables || [],
            frameCap: frameCap,
            recordedInputs: recordedInputs || [],
            discoverPaths: !!discoverPaths
          }
        };
        if (stagedMode) { load.mode = stagedMode; }
        post(load);
        post({ type: 'runner:run' });
      });
    },
    // code-challenge-behavioral-structural: run the live buffer ONCE through the
    // instrumented behavioural-trace harness (console/`python` challenges) and
    // resolve with { stdout, finalValues, behavioral, structural }. Re-stages the
    // live buffer with an additive `challengeTrace` load option then Runs —
    // mirrors requestChallengeStep's synchronous staging. The run streams stdout
    // live (visible Check). Never rejects: a runner failure leaves the promise
    // pending until the caller's own watchdog/terminal-state handling fires
    // (challenge-chrome.js / challenge-record-chrome.js drive it off runner state).
    requestChallengeTrace: function (variables, recordedInputs, challengeSeed) {
      return new Promise(function (resolve) {
        pendingChallengeTraceResolve = resolve;
        if (!loaded || runBlocked) { return; }
        if (consoleEl) { consoleEl.textContent = ''; }
        hideStdin();
        const files = collectFiles();
        const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
        const load = {
          type: 'runner:load',
          source: main ? main.content : editor.getValue(),
          files: files,
          assetBaseUrl: stagedAssets.assetBaseUrl || '',
          assetManifest: stagedAssets.assetManifest || [],
          challengeSeed: challengeSeed,
          challengeTrace: {
            variables: variables || [],
            // Demonstrated canned stdin so an input()-reading program does not block
            // the headless trace on the interactive bridge (fed via EOF-on-exhaust).
            // Normalised from recorded event objects / stored strings to plain lines.
            recordedInputs: toStdinLines(recordedInputs)
          }
        };
        if (stagedMode) { load.mode = stagedMode; }
        post(load);
        post({ type: 'runner:run' });
      });
    },
    // code-challenge-function-io: run the live buffer ONCE through the shared
    // instrumented function-call pass and resolve with { results, functions }.
    // `calls` is a batch of { functionName, args } run in one warm exec (the
    // module is exec'd with __name__ != "__main__"). `options.listFunctions`
    // signals a signatures-only fetch (the record chrome, typically with an
    // empty `calls`). Re-stages the live buffer with an additive `challengeFncall`
    // load option then Runs — mirrors requestChallengeTrace's synchronous
    // staging; the run streams per-call stdout live (visible Check). Never
    // rejects: a runner failure leaves the promise pending until the caller's own
    // watchdog/terminal-state handling fires (challenge-chrome.js /
    // challenge-record-chrome.js drive it off runner state).
    requestFunctionCall: function (calls, challengeSeed, options) {
      const opts2 = options || {};
      return new Promise(function (resolve) {
        pendingFunctionCallResolve = resolve;
        if (!loaded || runBlocked) { return; }
        if (consoleEl) { consoleEl.textContent = ''; }
        hideStdin();
        const files = collectFiles();
        const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
        const load = {
          type: 'runner:load',
          source: main ? main.content : editor.getValue(),
          files: files,
          assetBaseUrl: stagedAssets.assetBaseUrl || '',
          assetManifest: stagedAssets.assetManifest || [],
          challengeSeed: challengeSeed,
          challengeFncall: {
            calls: calls || [],
            listFunctions: !!opts2.listFunctions
          }
        };
        if (stagedMode) { load.mode = stagedMode; }
        post(load);
        post({ type: 'runner:run' });
      });
    },
    // code-challenge-exception-checkpoints: run the live buffer ONCE through the
    // shared canned-stdin console-run pass and resolve with { stdout, exception,
    // inputReads, outputAfterLastInput, ranOutOfInput }. `recordedInputs` is the
    // canned stdin array fed to the student program (exec'd AS __main__), one line
    // per input() call. Re-stages the live buffer with an additive
    // `challengeConsoleRun` load option then Runs — mirrors requestFunctionCall's
    // synchronous staging; the run streams stdout live (visible Check). Never
    // rejects: a runner failure leaves the promise pending until the caller's own
    // watchdog/terminal-state handling fires (challenge-chrome.js /
    // challenge-record-chrome.js drive it off runner state).
    requestConsoleRun: function (recordedInputs, challengeSeed) {
      return new Promise(function (resolve) {
        pendingConsoleRunResolve = resolve;
        if (!loaded || runBlocked) { return; }
        if (consoleEl) { consoleEl.textContent = ''; }
        hideStdin();
        const files = collectFiles();
        const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
        const load = {
          type: 'runner:load',
          source: main ? main.content : editor.getValue(),
          files: files,
          assetBaseUrl: stagedAssets.assetBaseUrl || '',
          assetManifest: stagedAssets.assetManifest || [],
          challengeSeed: challengeSeed,
          challengeConsoleRun: {
            recordedInputs: toStdinLines(recordedInputs)
          }
        };
        if (stagedMode) { load.mode = stagedMode; }
        post(load);
        post({ type: 'runner:run' });
      });
    },
    // trace-table-capture: run the live buffer ONCE through the instrumented
    // per-line trace-capture pass and resolve with { variables, rows, truncated,
    // stdout, error, outcome }. An AUTHORING convenience (the /trace-capture
    // surface), not grading. Re-stages the live buffer with an additive
    // `traceCapture` load option then Runs — mirrors requestConsoleRun's
    // synchronous staging; the run streams stdout live. Never rejects: a runner
    // failure leaves the promise pending until the caller's own watchdog /
    // terminal-state handling fires (trace-capture-chrome.js drives it off runner
    // state + a wall-clock timeout).
    requestTraceCapture: function () {
      return new Promise(function (resolve) {
        pendingTraceCaptureResolve = resolve;
        if (!loaded || runBlocked) { return; }
        if (consoleEl) { consoleEl.textContent = ''; }
        hideStdin();
        const files = collectFiles();
        const main = files.filter(function (f) { return f.name === 'main.py'; })[0];
        const load = {
          type: 'runner:load',
          source: main ? main.content : editor.getValue(),
          files: files,
          assetBaseUrl: stagedAssets.assetBaseUrl || '',
          assetManifest: stagedAssets.assetManifest || [],
          traceCapture: {}
        };
        if (stagedMode) { load.mode = stagedMode; }
        post(load);
        post({ type: 'runner:run' });
      });
    },
    // The runner iframe element itself — same-origin, so the Code Challenge
    // replay engine can dispatch synthetic keyboard/mouse DOM events directly
    // onto its canvas (the runner's OWN existing input wiring converts and
    // forwards them exactly as it does for a real user gesture — no new
    // postMessage type needed for that path) and can post a raw `runner:stdin`
    // line for console replay (already a host-facing message).
    getRunnerFrame: function () { return frame; },
    // Force-kill: reloading the iframe terminates a wedged worker (stronger than
    // runner:stop). The runner re-emits runner:ready, which re-stages the buffer.
    // Delegates to the shared restart() (re-navigates via the stored runnerSrc,
    // re-arms the startup timer, resets heardFromRunner/loaded, drops any Reload).
    restart: function () { restart(); },
    on: function (type, cb) {
      if (listeners[type]) { listeners[type].push(cb); }
      return this;
    },
    off: function (type, cb) {
      if (listeners[type]) {
        listeners[type] = listeners[type].filter(function (fn) { return fn !== cb; });
      }
      return this;
    },
    getEditor: function () { return editor; },
    // The name of the file currently shown in the editor (code-authenticity-snapshots:
    // the capture stamps a paste's location.file with this).
    getActiveFile: function () { return activeFile; },
    // True only while editor-core is programmatically staging content (see `loading`).
    // The authenticity capture uses this to exclude programmatic loads from its paste /
    // large-insert backstop.
    isLoading: function () { return loading; },
    // Whether the editor buffer is read-only (clientside-teacher-review). Run/Stop
    // remain live regardless — read-only is view+re-run, not view-only-static.
    isReadOnly: function () { return readOnly; },
    destroy: function () {
      win.removeEventListener('message', onMessage);
      // fix-runner-run-button-ready: cancel any pending startup timer and drop the
      // Reload affordance so a surface torn down during the startup window cannot
      // fire the timer against detached DOM.
      clearStartupTimer();
      removeReloadButton();
      if (runBtn) { runBtn.removeEventListener('click', run); }
      if (stopBtn) { stopBtn.removeEventListener('click', stop); }
      if (a11yEl && a11yElHandler) { a11yEl.removeEventListener(a11yElEvent, a11yElHandler); }
      // Drop the tab buttons (their click listeners go with the removed nodes).
      if (tabsEl) { while (tabsEl.firstChild) { tabsEl.removeChild(tabsEl.firstChild); } }
      // Remove the inline file-name prompt (its submit/keydown listeners go with it).
      if (filePrompt && filePrompt.wrap && filePrompt.wrap.parentNode &&
          filePrompt.wrap.parentNode.removeChild) {
        filePrompt.wrap.parentNode.removeChild(filePrompt.wrap);
        filePrompt = null;
      }
      // Remove the interactive-input row (its submit listener goes with the node).
      if (stdinWrap && stdinWrap.parentNode && stdinWrap.parentNode.removeChild) {
        stdinWrap.parentNode.removeChild(stdinWrap);
        stdinWrap = null;
        stdinInput = null;
      }
    }
  };
}
